import { Controller } from "@hotwired/stimulus"
import * as echarts from "echarts/core"
import { LineChart } from "echarts/charts"
import { GridComponent, TooltipComponent, LegendComponent } from "echarts/components"
import { CanvasRenderer } from "echarts/renderers"
import { ABSENT_VALUE, isAbsent } from "../../helpers/chart_values"

echarts.use([LineChart, GridComponent, TooltipComponent, LegendComponent, CanvasRenderer])

// Experiment detail chart: metric tabs (Spend/Conversions/Conv. Value/CPA) each plot one
// line per arm (Control + every test arm). Spend reads straight off the spend series; the
// other metrics read from byAction[activeAction][metricKey][arm] and only apply once an
// action selector exists (hidden entirely when the experiment has no conversion actions).
export default class extends Controller {
  static targets = ["canvas", "metricTab", "actionSelect"]
  static values = {
    categories: Array,
    arms: Array, // [{name, color}]
    spend: Object, // {arm => [..]}
    metrics: Array, // [{key, label, format}]
    actions: Array, // [name, ...]
    byAction: Object, // {action: {conversions: {arm: []}, conversions_value: {...}, cpa: {...}}}
    activeMetric: { type: Number, default: 0 },
    activeAction: { type: Number, default: 0 },
  }

  connect() {
    this.chart = echarts.init(this.canvasTarget)
    this.toggleActionSelect()
    this.render()
    this.resizeObserver = new ResizeObserver(() => this.chart.resize())
    this.resizeObserver.observe(this.canvasTarget)
  }

  disconnect() {
    this.resizeObserver?.disconnect()
    this.chart?.dispose()
  }

  selectMetric(event) {
    const index = Number(event.currentTarget.dataset.index)
    if (index === this.activeMetricValue) return
    this.activeMetricValue = index
    this.metricTabTargets.forEach((tab, i) => tab.classList.toggle("active", i === index))
    this.toggleActionSelect()
    this.render()
  }

  selectAction(event) {
    this.activeActionValue = Number(event.currentTarget.value)
    this.render()
  }

  toggleActionSelect() {
    if (!this.hasActionSelectTarget) return
    const metric = this.metricsValue[this.activeMetricValue]
    this.actionSelectTarget.classList.toggle("hidden", metric.key === "spend")
  }

  render() {
    const metric = this.metricsValue[this.activeMetricValue]
    if (!metric) return

    const seriesFor = (armName) => {
      if (metric.key === "spend") return this.spendValue[armName] || []
      const actionName = this.actionsValue[this.activeActionValue]
      const byMetric = (this.byActionValue[actionName] || {})[metric.key] || {}
      return byMetric[armName] || []
    }

    const lines = this.armsValue.map((arm) => ({
      type: "line",
      name: arm.name,
      data: seriesFor(arm.name),
      smooth: true,
      showSymbol: false,
      lineStyle: { color: arm.color, width: 2 },
      itemStyle: { color: arm.color },
    }))

    this.chart.setOption({
      legend: { top: 0, icon: "roundRect", textStyle: { color: "#55615F", fontFamily: "Aeonik Fono, monospace", fontSize: 11 } },
      grid: { left: 4, right: 16, top: 32, bottom: 4, containLabel: true },
      tooltip: { trigger: "axis", valueFormatter: (v) => this.format(v, metric.format) },
      xAxis: { type: "category", data: this.categoriesValue, boundaryGap: false, axisLabel: { color: "#9CA6A4", fontFamily: "Aeonik Fono, monospace", fontSize: 11 } },
      yAxis: { type: "value", axisLabel: { color: "#9CA6A4", fontFamily: "Aeonik Fono, monospace", fontSize: 11, formatter: (v) => this.format(v, metric.format) }, splitLine: { lineStyle: { color: "#EDEFEF" } } },
      series: lines,
    }, true)
  }

  // Tooltip and axis labels. See helpers/chart_values for why an absent value can't be detected
  // with a null check alone — a nil CPA arrives here as NaN, and read "$NaN" before this guard.
  format(value, format) {
    if (isAbsent(value)) return ABSENT_VALUE
    if (format === "currency") return `$${Number(value).toLocaleString(undefined, { maximumFractionDigits: 0 })}`
    return Number(value).toLocaleString(undefined, { maximumFractionDigits: 1 })
  }
}
